import React, { useContext, useState, useEffect, useRef, useCallback } from "react";
import MapContext from "../MapContext";
import { Point } from "ol/geom";
import { Feature } from "ol";
import { getMapLayerById } from "../utils/mapUtils";
import Overlay from "ol/Overlay.js";
import "./PlotCoordinates.css";
import { Button } from "@progress/kendo-react-buttons";
import { useSelector } from "react-redux";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import { createStringXY, toStringHDMS } from "ol/coordinate.js";
import { transform } from "ol/proj";
import { MousePosition } from "ol/control";
import { Input } from "@progress/kendo-react-inputs";
import proj4 from "proj4";

/**
 * @typedef {Object} CoordinateObject
 * @property {number[]} coordinate - An array of two numbers representing the x and y coordinates.
 * @property {string} id - The ID of the object.
 */

const PlotCoordinates = () => {
    const mapConfig = useSelector((state) => state.configData.mapConfig);
    const mousePositionElement = useRef();
    const mousePositionRef = useRef();
    const firstRender = useRef(true);
    const [open, setOpen] = useState(true);
    const { map } = useContext(MapContext);
    /** @type {CoordinateObject[] | []}  */
    const [coordinateFeatures, setCoordinateFeatures] = useState([]);

    const extent = useSelector((state) => state.configData.mapConfig.map.fullExtent);
    const extentArray = extent.split(",").map(Number);

    const [selectedFeature, setSelectedFeature] = useState();
    const index = useRef(1);
    const [isTyping, setIsTyping] = useState(false);

    /**
     * Checks if the coordinates (x, y) are inside the given extent.
     *
     * @param {number} x - The x-coordinate.
     * @param {number} y - The y-coordinate.
     * @param {number[]} extentArray - An array representing the extent in the format [xMin, yMin, xMax, yMax].
     * @return {boolean} Returns true if the coordinates are inside the extent, false otherwise.
     */
    const isInsideExtent = (x, y, extentArray) => {
        return (
            x >= extentArray[0] &&
            x <= extentArray[2] &&
            y >= extentArray[1] &&
            y <= extentArray[3]
        );
    };

    const wkids = [
        { id: "lat/lng", text: "4326 Lat/Long", proj: "EPSG:4326" },
        {
            id: mapConfig.map.projection,
            text: mapConfig.map.projection.split(":")[1],
            proj: mapConfig.map.projection,
        },
        // { id: "ddm", text: "4326 DDM", proj: "EPSG:4326" },
        // { id: "dms", text: "4326 DMS", proj: "EPSG:4326" },
        // { id: "x/y", text: "3857 X/Y", proj: "EPSG:3857" },
    ];

    const [selectedOption, setSelectedOption] = useState({
        id: "lat/lng",
        text: "4326 Lat/Long",
        proj: "EPSG:4326",
    });
    const selectedRef = useRef({
        id: "lat/lng",
        text: "4326 Lat/Long",
        proj: "EPSG:4326",
    });

    const handleChange = (e) => {
        selectedRef.current = e.target.value;
        setSelectedOption(e.target.value);
    };

    const decideCoordinateFormat = () => {
        let format;
        if (selectedRef.current.id === "dms") {
            format = toStringHDMS;
        } else if (selectedRef.current.id === "lat/lng") {
            format = createStringXY(4);
        } else if (selectedRef.current.id === mapConfig.map.projection) {
            format = createStringXY(2);
        }
        return format;
    };

    function dmsToDecimal(degrees, minutes, seconds, direction) {
        let decimal = degrees + minutes / 60 + seconds / 3600;

        // If the direction is South or West, make the decimal negative
        if (direction === "S" || direction === "W") {
            decimal = decimal * -1;
        }

        return decimal;
    }

    const handleOpen = () => {
        setOpen(!open);
        setX("");
        setY("");
    };

    const checkForCoordinateFeatures = useCallback(() => {
        const coordinateSource = getMapLayerById(map, "plotCoordinateLayer").getSource();
        const newCoordinateFeatures = coordinateSource
            .getFeatures()
            .map((feature, index) => {
                return {
                    coordinate: feature.values_.geometry.flatCoordinates,
                    id: feature.ol_uid,
                    index: index + 1,
                };
            });
        if (selectedRef.current.proj !== map.getView().getProjection().getCode()) {
            newCoordinateFeatures.forEach((feature) => {
                feature.coordinate = transform(
                    feature.coordinate,
                    selectedRef.current.proj,
                    "EPSG:4326"
                );
            });
        }

        return newCoordinateFeatures;
    }, [map]);

    const [x, setX] = useState("");
    const [y, setY] = useState("");

    const handleDelete = () => {
        const coordinateSource = getMapLayerById(map, "plotCoordinateLayer").getSource();
        let featureIds = coordinateSource.getFeatures().map((feature) => feature.getId());
        coordinateSource.clear();
        coordinateFeatures.forEach((feature) => {
            const id = featureIds[feature.index - 1];
            const overlay = map.getOverlayById(id);
            map.removeOverlay(overlay);
        });
        setCoordinateFeatures([]);
        index.current = 1;
    };

    // Function to add a feature to a source

    const addFeatureToSource = (source, coordinate) => {
        const feature = new Feature({
            geometry: new Point(coordinate),
        });
        feature.setId(index.current);
        source.addFeature(feature);
        index.current = index.current + 1;
        return feature;
    };

    // Function to add an overlay to a map
    const addOverlayToMap = (map, id, coordinate, htmlContent) => {
        const element = document.createElement("div");
        element.innerHTML = htmlContent;

        const overlay = new Overlay({
            element: element,
            position: coordinate,
            positioning: "bottom-center", // Change this to 'bottom-center'
            id: id,
        });

        map.addOverlay(overlay);

        // // Calculate the pixel offset needed to center the overlay
        // const overlayHeight = element.offsetHeight;
        // const overlayWidth = element.offsetWidth;
        // const pixelOffset = [overlayWidth / 2, overlayHeight];

        // // Apply the pixel offset to the overlay's position
        // overlay.setOffset(pixelOffset);

        return overlay; // return the overlay, in case you need to use it later
    };

    useEffect(() => {
        if (selectedRef.current.proj !== map.getView().getProjection().getCode()) {
            setCoordinateFeatures(checkForCoordinateFeatures());
        }
    }, [selectedRef.current.proj]);

    const mapClickHandler = useCallback(
        (e) => {
            const coordinate = e.coordinate;

            const mapLayer = getMapLayerById(map, "plotCoordinateLayer");
            const coordinateLayerSource = mapLayer.getSource();

            // Add feature
            const feature = addFeatureToSource(coordinateLayerSource, coordinate);

            const newCoordinateFeatures = checkForCoordinateFeatures();

            let featureId = Number(feature.getId());

            // Handle HTML popup
            addOverlayToMap(
                map,
                feature.getId(),
                coordinate,
                customHTMLPopup(coordinate, selectedRef, mapConfig, featureId)
            );

            setCoordinateFeatures(newCoordinateFeatures);
        },
        [checkForCoordinateFeatures, setCoordinateFeatures]
    );
    const handleAdd = useCallback(() => {
        let coordinate = [parseFloat(x), parseFloat(y)];

        decideCoordinateFormat(coordinate);
        let inputProjection;

        if (selectedRef.current.id === "dms") {
            inputProjection = "EPSG:4326";
            const [latDegrees, latMinutes, latSeconds] = x.split(" ");
            const [lngDegrees, lngMinutes, lngSeconds] = y.split(" ");

            const latDecimal = dmsToDecimal(
                parseFloat(latDegrees),
                parseFloat(latMinutes),
                parseFloat(latSeconds),
                "N"
            );
            const lngDecimal = dmsToDecimal(
                parseFloat(lngDegrees),
                parseFloat(lngMinutes),
                parseFloat(lngSeconds),
                "E"
            );

            coordinate = [lngDecimal, latDecimal];
        } else if (selectedRef.current.id === "lat/lng") {
            inputProjection = "EPSG:4326";
        } else if (selectedRef.current.id === mapConfig.map.projection) {
            inputProjection = mapConfig.map.projection;
        }

        const outputProjection = map.getView().getProjection().getCode(); // The map's projection

        // Transform the coordinate
        let transformedCoordinate = proj4(inputProjection, outputProjection, coordinate);

        const mapLayer = getMapLayerById(map, "plotCoordinateLayer");
        const coordinateLayerSource = mapLayer.getSource();

        // Add feature
        const feature = addFeatureToSource(coordinateLayerSource, transformedCoordinate);
        const newCoordinateFeatures = checkForCoordinateFeatures();
        let featureId = Number(feature.getId());

        // Handle HTML popup

        addOverlayToMap(
            map,
            feature.getId(),
            transformedCoordinate, // Use the transformed coordinate for the popup
            customHTMLPopup(transformedCoordinate, selectedRef, mapConfig, featureId)
        );

        setCoordinateFeatures(newCoordinateFeatures);

        // Get the map view
        const view = map.getView();

        // Set the center of the view to the new coordinate
        view.setCenter(transformedCoordinate);

        // Set a specific zoom level
        const zoomLevel = 20; // Adjust this value as needed
        view.setZoom(zoomLevel);

        setX("");
        setY("");
    }, [map, x, y, selectedRef, mapConfig, setCoordinateFeatures]);

    useEffect(() => {
        if (!map) {
            return;
        }
        const newCoordinateFeatures = checkForCoordinateFeatures();
        setCoordinateFeatures(newCoordinateFeatures);
        map.getViewport().style.cursor = "crosshair";
        map.on("click", mapClickHandler);
        return () => {
            map.getViewport().style.cursor = "default";
            map.un("click", mapClickHandler);
        };
    }, [map, checkForCoordinateFeatures, mapClickHandler]);

    useEffect(() => {
        if (!selectedFeature) {
            setSelectedFeature();
        }
    }, [selectedFeature]);

    useEffect(() => {
        if (selectedRef.current) {
            map.getOverlays().forEach((overlay) => {
                const originalCoordinate = overlay.getPosition();
                const index = overlay.getId();

                overlay.getElement().innerHTML = customHTMLPopup(
                    originalCoordinate,
                    selectedRef,
                    mapConfig,
                    index
                );
            });
        }
    }, [selectedRef.current.id]); // Run this hook whenever `selectedRef.current.id` changess hook whenever `selectedRef`

    useEffect(() => {
        let lastKnownCoordinate = [null, null]; // Initialize with null values

        const updateCoordinates = (e) => {
            e.preventDefault();
            if (!isTyping) {
                const pixel = [e.clientX, e.clientY];
                let coordinate;
                if (map.getTargetElement().contains(e.target)) {
                    coordinate = map.getCoordinateFromPixel(pixel);
                    if (selectedRef.current.id === "lat/lng") {
                        coordinate = transform(
                            coordinate,
                            mapConfig.map.projection,
                            "EPSG:4326"
                        );
                    }
                } else {
                    coordinate = lastKnownCoordinate; // Use the last known coordinate
                }
                setX(coordinate[0] !== null ? coordinate[0] : ""); // Check for null and display ""
                setY(coordinate[1] !== null ? coordinate[1] : ""); // Check for null and display ""
                lastKnownCoordinate = coordinate; // Update last known coordinate
            }
        };

        window.addEventListener("mousemove", updateCoordinates);

        return () => {
            window.removeEventListener("mousemove", updateCoordinates);
        };
    }, [map, isTyping]);

    let sortedArray = coordinateFeatures.sort((a, b) => a.id - b.id);

    return (
        <>
            <h3 className="title">Plot Coordinates</h3>

            <div>
                <DropDownList
                    style={{
                        width: "fit-content",
                    }}
                    data={wkids}
                    dataItemKey="id"
                    textField="text"
                    value={selectedOption}
                    onChange={(e) => handleChange(e)}
                />
            </div>
            <div>
                {selectedRef.current.id === "dms" && (
                    <>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <Input
                                type="text"
                                value={x}
                                onChange={(e) => setX(e.target.value)}
                                placeholder="Degrees"
                                style={{ width: "20%" }}
                            />
                            <span>° </span>
                            <Input
                                type="text"
                                placeholder="Minutes"
                                style={{ width: "20%" }}
                            />
                            <span>' </span>
                            <Input
                                type="text"
                                placeholder="Seconds"
                                style={{ width: "20%" }}
                            />
                            <span>" </span>
                            <select style={{ width: "20%" }}>
                                <option>N</option>
                                <option>S</option>
                            </select>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <Input
                                type="text"
                                placeholder="Degrees"
                                style={{ width: "20%" }}
                            />
                            <span>° </span>
                            <Input
                                type="text"
                                placeholder="Minutes"
                                style={{ width: "20%" }}
                            />
                            <span>' </span>
                            <Input
                                type="text"
                                placeholder="Seconds"
                                style={{ width: "20%" }}
                            />
                            <span>" </span>
                            <select style={{ width: "20%" }}>
                                <option>E</option>
                                <option>W</option>
                            </select>
                        </div>
                    </>
                )}
                {selectedRef.current.id === "lat/lng" && (
                    <>
                        <NumericTextBox
                            onFocus={() => setIsTyping(true)}
                            onBlur={() => setIsTyping(false)}
                            format={"0.000000"}
                            value={parseFloat(x)}
                            onChange={(e) => setX(parseFloat(e.target.value))}
                            placeholder="Enter Latitude"
                        />
                        <NumericTextBox
                            onFocus={() => setIsTyping(true)}
                            onBlur={() => setIsTyping(false)}
                            format={"0.000000"}
                            value={parseFloat(y)}
                            onChange={(e) => setY(parseFloat(e.target.value))}
                            placeholder="Enter Longitude"
                        />
                    </>
                )}
                {selectedRef.current.id === mapConfig.map.projection && (
                    <>
                        <NumericTextBox
                            onFocus={() => setIsTyping(true)}
                            onBlur={() => setIsTyping(false)}
                            format={"0.000000"}
                            value={parseFloat(x)}
                            onChange={(e) => setX(parseFloat(e.target.value))}
                            placeholder="Enter X coordinate"
                        />
                        <NumericTextBox
                            onFocus={() => setIsTyping(true)}
                            onBlur={() => setIsTyping(false)}
                            format={"0.000000"}
                            value={parseFloat(y)}
                            onChange={(e) => setY(parseFloat(e.target.value))}
                            placeholder="Enter Y coordinate"
                        />
                    </>
                )}
            </div>

            <Button onClick={handleAdd}>Add</Button>

            <Button onClick={handleDelete}>Clear Coordinates</Button>

            {coordinateFeatures.map((feature, index) => {
                // Always transform the coordinates to EPSG:4326 before formatting them with toStringHDMS
                let coordinate = transform(
                    feature.coordinate,
                    map.getView().getProjection().getCode(),
                    "EPSG:4326"
                );

                let formattedCoordinate;
                if (selectedRef.current.id === "dms") {
                    // Format the coordinates as Degrees, Minutes, Seconds
                    formattedCoordinate = toStringHDMS(coordinate);
                } else {
                    // Transform the coordinates to the selected projection for other formats
                    coordinate = transform(
                        coordinate,
                        "EPSG:4326",
                        selectedRef.current.proj
                    );
                    formattedCoordinate = `X: ${coordinate[0].toFixed(
                        6
                    )}, Y: ${coordinate[1].toFixed(6)}`;
                }

                const handleClickList = () => {
                    setSelectedFeature(feature.id);

                    map.getView().setZoom(20);
                    map.getView().setCenter(feature.coordinate);
                };

                // const handleDeleteListItem = (id) => {
                //     // Filter out the coordinate item with the specified id
                //     console.log(id);
                //     const updatedCoordinateFeatures = coordinateFeatures.filter(
                //         (feature) => feature.id !== id
                //     );

                //     // Remove the feature from the map's layer source
                //     const coordinateSource = getMapLayerById(
                //         map,
                //         "plotCoordinateLayer"
                //     ).getSource();
                //     const featureToRemove = coordinateSource.getFeatureById(id);
                //     if (featureToRemove) {
                //         coordinateSource.removeFeature(featureToRemove);
                //     }

                //     // Remove the overlay associated with the feature
                //     const overlay = map.getOverlayById(id);
                //     if (overlay) {
                //         map.removeOverlay(overlay);
                //     }

                //     // Update the state to remove the deleted coordinate item
                //     setCoordinateFeatures(updatedCoordinateFeatures);
                // };

                // Apply a 'selected' class to the clicked item
                const itemClass =
                    feature.id === selectedFeature
                        ? "coordinate-list selected"
                        : "coordinate-list";

                return (
                    <div
                        key={feature.id}
                        className={`${itemClass} coordinate-list:hover`} // Add the 'selected' class to the clicked itemClass}
                        onClick={handleClickList}>
                        <div className="coordinate-label">{index + 1}</div>
                        <div className="coordinate">{formattedCoordinate}</div>
                        {/* <Button onClick={() => handleDeleteListItem(feature.id)}>
                            Delete
                        </Button> */}
                    </div>
                );
            })}
        </>
    );
};

export default PlotCoordinates;

const customHTMLPopup = (coordinate, selectedRef, mapConfig, index) => {
    // Always transform the coordinates to EPSG:4326 before formatting them with toStringHDMS
    let newCoordinate = transform(coordinate, mapConfig.map.projection, `EPSG:4326`);

    let formattedCoordinate;
    if (selectedRef.current.id === "dms") {
        let transformedCoordinate = transform(
            coordinate,
            mapConfig.map.projection,
            `EPSG:4326`
        );
        formattedCoordinate = toStringHDMS(transformedCoordinate);
        return `<div class="popup">
    <span style="display: inline; white-space: nowrap;">${index}</span>
    <span style="display: inline; white-space: nowrap;">${formattedCoordinate}</span>
    </div>`;
    } else if (selectedRef.current.id === "lat/lng") {
        formattedCoordinate =
            "X:" +
            newCoordinate[0].toFixed(6) +
            ", " +
            "Y:" +
            newCoordinate[1].toFixed(6);
        return `<div class="popup">
    <span style="display: inline; white-space: nowrap;">${index}</span>
    <span style="display: inline; white-space: nowrap;">${formattedCoordinate}</span>
    </div>`;
    } else if (selectedRef.current.id === mapConfig.map.projection) {
        formattedCoordinate = createStringXY(2)(coordinate);
        const [x, y] = formattedCoordinate.split(", ");
        formattedCoordinate = `X: ${x}, Y: ${y}`;
        return `<div class="popup">
        <span style="display: inline; white-space: nowrap;">${index}</span>
        <span style="display: inline; white-space: nowrap;">${formattedCoordinate}</span>
    </div>`;
    }

    // Split the formatted coordinate into X and Y components
    const [x, y] = formattedCoordinate.split(", ");

    return `<div class="popup">
    <span style="display: inline; white-space: nowrap;">${index}</span>
    <span style="display: inline; white-space: nowrap;">Lat:${y}, Lng:${x}</span>
    
</div>`;
};
